import { inject, signal } from '@angular/core';
import { LoggerAssociation } from './interfaces';
import {
  BehaviorSubject,
  Observable,
  debounceTime,
  distinctUntilChanged,
  firstValueFrom,
  map,
  switchMap,
  withLatestFrom,
} from 'rxjs';
import { ListCrmObjectsRequestInterface } from '@vendasta/crm';
import { CrmInjectionToken, ObjectType } from '../../../../tokens-and-interfaces';
import { TableCustomizationService, CrmObjectService } from '../../../../shared-services';

export abstract class CrmActivityLoggerAssociationField<T> {
  protected abstract customizationService: TableCustomizationService;
  protected abstract crmObjectType: T;

  protected readonly config = inject(CrmInjectionToken);
  protected readonly crmObjectService = inject(CrmObjectService);
  protected readonly associationsList = signal<LoggerAssociation[]>([]);
  protected readonly searchTerm$$ = new BehaviorSubject<string>('');
  protected readonly options$ = this.setupSearchTerm();

  protected abstract fetchObjects(objectIds: string[]): Promise<LoggerAssociation[]>;
  protected abstract setupSearchTerm(): Observable<LoggerAssociation[]>;

  protected async setAssociations(associations: LoggerAssociation[]): Promise<void> {
    const existingAssociations = this.associationsList();
    // Infer existing names from existing associations
    associations
      .filter((association) => !association.name)
      .forEach((association) => {
        const existing = existingAssociations.find((item) => item.crmObjectId === association.crmObjectId);
        association.name = existing?.name || '';
      });
    // fetch missing names
    const objectsToFetch = associations
      .filter((association) => !association.name)
      .map((association) => association.crmObjectId!);
    if (objectsToFetch.length > 0) {
      const objects = await this.fetchObjects(objectsToFetch);
      objects.forEach((object) => {
        const association = associations.find((item) => item.crmObjectId === object.crmObjectId);
        association!.name = object.name;
      });
    }
    this.associationsList.set(associations);
  }

  protected updateSearchTerm(searchTerm?: unknown): void {
    if (typeof searchTerm === 'string') {
      this.searchTerm$$.next(searchTerm.trim());
    } else {
      this.searchTerm$$.next('');
    }
  }
}

export abstract class CrmActivityLoggerObjectAssociationField extends CrmActivityLoggerAssociationField<ObjectType> {
  protected async fetchObjects(objectIds: string[]): Promise<LoggerAssociation[]> {
    const crmObjs = await firstValueFrom(this.crmObjectService.getMultiObject(this.crmObjectType, objectIds));
    return (
      crmObjs?.crmObjects?.map(
        (obj) =>
          ({
            name: this.customizationService.getRowFullName(obj),
            crmObjectId: obj.crmObjectId,
            crmObjectType: this.crmObjectType,
          } as LoggerAssociation),
      ) ?? []
    );
  }

  protected setupSearchTerm(): Observable<LoggerAssociation[]> {
    return this.searchTerm$$.asObservable().pipe(
      debounceTime(500),
      distinctUntilChanged(),
      withLatestFrom(this.config.namespace$),
      map(
        ([searchTerm, namespace]) =>
          ({
            namespace,
            search: { searchTerm },
            pagingOptions: { cursor: '', pageSize: 10 },
          } as ListCrmObjectsRequestInterface),
      ),
      switchMap((r: ListCrmObjectsRequestInterface) => this.crmObjectService.listObjects(this.crmObjectType, r)),
      map(
        (filteredOptions) =>
          filteredOptions?.crmObjects?.map(
            (option) =>
              ({
                name: this.customizationService.getRowFullName(option),
                associationInfo: this.customizationService.getAssociationInfo(option),
                crmObjectId: option.crmObjectId,
                crmObjectType: this.crmObjectType,
              } as LoggerAssociation),
          ) || [],
      ),
    );
  }
}
